import MarkdownRenderer from "./MarkdownRenderer";

## Basic Usage

<MarkdownRenderer>{`# Hello

A short paragraph with **bold** and *italic* text, plus a [link](https://example.com).

- one
- two
- three`}</MarkdownRenderer>

---

## Headings

<MarkdownRenderer>{`# Heading 1
## Heading 2
### Heading 3
#### Heading 4
##### Heading 5
###### Heading 6`}</MarkdownRenderer>

---

## Text formatting

<MarkdownRenderer>{`**Bold**, *italic*, ***bold italic***, ~~strikethrough~~, and \`inline code\` all render distinctly.

You can also combine **bold with [a link](https://example.com)** or *italic with \`code\`* inline.`}</MarkdownRenderer>

---

## Lists

<MarkdownRenderer>{`### Unordered

- First item
- Second item
  - Nested item A
  - Nested item B
    - Deeply nested
- Third item

### Ordered

1. Step one
2. Step two
   1. Sub-step
   2. Sub-step
3. Step three

### Task list (GFM)

- [x] Completed task
- [x] Another completed task
- [ ] Pending task
- [ ] Another pending task`}</MarkdownRenderer>

---

## Code blocks

<MarkdownRenderer>{`Inline \`code\` looks like this. Fenced blocks render with syntax styling:

\`\`\`ts
function greet(name: string): string {
  return \`Hello, \${name}!\`;
}

const message = greet("world");
console.log(message);
\`\`\`

Multiple language hints work too:

\`\`\`tsx
import { MarkdownRenderer } from "ui/text/MarkdownRenderer";

export function Demo() {
  return <MarkdownRenderer>{"# Hi"}</MarkdownRenderer>;
}
\`\`\`

\`\`\`bash
yarn install
yarn higharc typecheck run
\`\`\``}</MarkdownRenderer>

---

## Blockquotes

<MarkdownRenderer>{`> A single-line blockquote.

> A multi-paragraph blockquote.
>
> The second paragraph stays inside the same quote block.
>
> > Nested quotes are also supported.`}</MarkdownRenderer>

---

## Tables

<MarkdownRenderer>{`| Component | Status | Notes |
| --------- | ------ | ----- |
| IconButton | shipped | sizes + styles match Button |
| LoadingHigharcIcon | shipped | respects prefers-reduced-motion |
| MarkdownRenderer | shipped | hardened against XSS |

Tables with alignment:

| Left | Center | Right |
| :--- | :----: | ----: |
| a | b | 1 |
| c | d | 22 |
| e | f | 333 |`}</MarkdownRenderer>

Tables wider than their container scroll horizontally:

<div style={{ maxWidth: "360px" }}>
<MarkdownRenderer>{`| Plan | Sheet size | Scale | Revision | Updated by |
| ---- | ---------- | ----- | -------- | ---------- |
| Aspen | 34×22" | 1/4" = 1' | R3 | jcarter@example.com |
| Birchwood | 34×22" | 1/8" = 1' | R1 | mreyes@example.com |`}</MarkdownRenderer>
</div>

---

## Links and images

<MarkdownRenderer>{`Inline [Higharc](https://higharc.com) link, an [autolink](https://example.com), and a reference-style [link][1].

![sample image](https://placehold.co/200x80/eee/444?text=Markdown+image)

[1]: https://example.com`}</MarkdownRenderer>

---

## Horizontal rules

<MarkdownRenderer>{`Above the rule.

---

Below the rule.

***

After a second rule.`}</MarkdownRenderer>

---

## Streaming mode

<MarkdownRenderer mode="streaming">{`Streaming mode is for content arriving token-by-token. It tolerates partial markdown safely. The renderer itself adds no visual decoration in streaming mode — host the typing indicator separately (e.g. a "thinking" placeholder) if needed.`}</MarkdownRenderer>

---

## Safety: dangerous markup is stripped

<MarkdownRenderer>{`The renderer ships rehype-harden, so the following are stripped at render time:

- \`<script>alert(1)</script>\` — script tags removed
- \`<a href="javascript:alert(1)">click</a>\` — javascript: URL neutralized
- \`<img src=x onerror="alert(1)">\` — event handlers removed

Safe HTML still renders:

This <strong>strong</strong> and <em>emphasis</em> embedded as raw HTML pass through.

Test inputs (these should all be inert):

<script>alert("xss")</script>

<a href="javascript:alert(1)">malicious link</a>

<img src=x onerror="alert(1)">`}</MarkdownRenderer>

---

## Themed via CSS custom properties

<div style={{ ["--markdown-link"]: "#715bc7", ["--markdown-code-bg"]: "#f0e8ff", padding: "1rem", border: "1px solid #eee", borderRadius: "0.5rem" }}>
  <MarkdownRenderer>{`Override \`--markdown-link\` and \`--markdown-code-bg\` on any ancestor to re-theme. [Try clicking](#) — link is purple, inline \`code\` background is purple-tinted.`}</MarkdownRenderer>
</div>
